// client.ts — типизированный HTTP-клиент read-API (Ф2). Все методы возвращают
// типы из contract.ts (zod-схемы сервера). fetch на тот же origin: dev — Vite
// проксирует /api на :3000, prod — Fastify раздаёт и SPA, и /api. 401 → ApiError,
// который ловит session-слой и показывает экран входа.

import type {
  AccessResponse,
  AnalyticsResponse,
  Assignment,
  AssignmentCreatePayload,
  AssignmentsResponse,
  AssignmentWritePayload,
  GrantAdminPayload,
  InviteResponse,
  ReportResponse,
  RespondentCreatePayload,
  RespondentInfoResponse,
  RespondentsResponse,
  RespondentUpdatePayload,
  RoleChangePayload,
  SurveyCreatePayload,
  SurveyListResponse,
  SurveyTreeResponse,
  SurveyWritePayload,
  TenantCreatePayload,
  TenantUpdatePayload,
  TodayResponse,
  WebRoleContract,
  WeekResponse,
} from './contract';

// Возможности веб-роли (Ф8). Зеркало Capability в src/lib/authz.ts — для скрытия
// пунктов меню/кнопок (сервер всё равно проверяет права на каждом эндпоинте).
export type Capability =
  | 'viewDashboard'
  | 'viewOthers'
  | 'analytics'
  | 'editSurveys'
  | 'editSchedule'
  | 'manageRespondents'
  | 'exportXlsx'
  | 'manageAccess'
  | 'manageTenants';

// /api/me не описан в серверном api-contract (это auth-, не read-эндпоинт),
// поэтому форму держим здесь, синхронно с src/api/routes/me.ts.
export interface MeResponse {
  respondentId: string;
  role: string;
  name: string;
  tenantId: string;
  webRole: WebRoleContract;
  capabilities: Capability[];
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!res.ok) {
    throw new ApiError(res.status, `GET /api${path} → ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Значение CSRF-куки (double-submit). Сервер ставит её при логине. */
function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)tgop_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

/** Текст ошибки из тела ответа ({error} от роутов) — для показа пользователю. */
async function errorText(res: Response, method: string, path: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return body.error;
  } catch {
    /* не JSON — падаем на дефолт */
  }
  return `${method} /api${path} → ${res.status}`;
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  payload?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { 'X-CSRF-Token': csrfToken() };
  // Тело — только когда есть: bodyless DELETE/POST иначе ловит 400 (пустой JSON).
  if (payload !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    ...(payload !== undefined ? { body: JSON.stringify(payload) } : {}),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await errorText(res, method, path));
  }
  return (await res.json()) as T;
}

function range(from?: string, to?: string): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  me: () => getJson<MeResponse>('/me'),
  today: () => getJson<TodayResponse>('/today'),
  week: () => getJson<WeekResponse>('/week'),
  report: (runId: string) => getJson<ReportResponse>(`/report/${encodeURIComponent(runId)}`),
  respondents: () => getJson<RespondentsResponse>('/respondents'),
  respondent: (id: string) =>
    getJson<RespondentInfoResponse>(`/respondents/${encodeURIComponent(id)}`),
  analytics: (from?: string, to?: string) =>
    getJson<AnalyticsResponse>(`/analytics${range(from, to)}`),
  // Конструктор анкет (Ф6, write).
  surveys: () => getJson<SurveyListResponse>('/surveys'),
  surveyTree: (id: string) => getJson<SurveyTreeResponse>(`/surveys/${encodeURIComponent(id)}`),
  createSurvey: (payload: SurveyCreatePayload) =>
    sendJson<SurveyTreeResponse>('POST', '/surveys', payload),
  saveSurvey: (id: string, payload: SurveyWritePayload) =>
    sendJson<SurveyTreeResponse>('PUT', `/surveys/${encodeURIComponent(id)}`, payload),
  // Расписание и напоминания (Ф7, write).
  assignments: () => getJson<AssignmentsResponse>('/assignments'),
  saveAssignment: (id: string, payload: AssignmentWritePayload) =>
    sendJson<Assignment>('PUT', `/assignments/${encodeURIComponent(id)}`, payload),
  createAssignment: (payload: AssignmentCreatePayload) =>
    sendJson<Assignment>('POST', '/assignments', payload),
  // Роли и доступы + tenant (Ф8, write, owner).
  access: () => getJson<AccessResponse>('/access'),
  grantAdmin: (payload: GrantAdminPayload) =>
    sendJson<{ ok: true }>('POST', '/access/admins', payload),
  revokeAdmin: (tgId: string) =>
    sendJson<{ ok: true }>('DELETE', `/access/admins/${encodeURIComponent(tgId)}`),
  createTenant: (payload: TenantCreatePayload) =>
    sendJson<{ id: string }>('POST', '/tenants', payload),
  updateTenant: (id: string, payload: TenantUpdatePayload) =>
    sendJson<{ ok: true }>('PUT', `/tenants/${encodeURIComponent(id)}`, payload),
  switchTenant: (tenantId: string) =>
    sendJson<{ ok: true; tenantId: string }>('POST', '/tenant/switch', { tenantId }),
  // Управление респондентами (Ф8, write, owner/director).
  changeRole: (id: string, payload: RoleChangePayload) =>
    sendJson<{ ok: true }>('PUT', `/respondents/${encodeURIComponent(id)}/role`, payload),
  createRespondent: (payload: RespondentCreatePayload) =>
    sendJson<InviteResponse>('POST', '/respondents', payload),
  reissueInvite: (id: string) =>
    sendJson<InviteResponse>('POST', `/respondents/${encodeURIComponent(id)}/invite`, {}),
  updateRespondent: (id: string, payload: RespondentUpdatePayload) =>
    sendJson<{ ok: true }>('PUT', `/respondents/${encodeURIComponent(id)}`, payload),
  // Экспорт — файл, не JSON: ссылку отдаём как есть (<a download> в Ф4).
  exportUrl: (from?: string, to?: string) => `/api/export${range(from, to)}`,
  logout: () =>
    fetch('/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      // logout — state-changing, поэтому за requireCsrf (как остальные write):
      // double-submit токен из читаемой куки. Защищает от forced-logout с чужого сайта.
      headers: { 'X-CSRF-Token': csrfToken() },
    }),
};
